// Billing codes for ABA therapy services
// Based on CPT codes for behavior analysis

export type BillingCodeType =
  | '97151' // Behavior identification assessment
  | '97152' // Behavior identification supporting assessment
  | '97153' // Adaptive behavior treatment by protocol
  | '97154' // Group adaptive behavior treatment by protocol
  | '97155' // Adaptive behavior treatment with protocol modification (BCBA)
  | '97156' // Family adaptive behavior treatment guidance
  | '97157' // Multiple family group adaptive behavior treatment guidance
  | '97158'; // Group adaptive behavior treatment with protocol modification

export type PlaceOfService =
  | '11' // Office
  | '12' // Home
  | '03' // School
  | '02' // Telehealth - Provided in Patient's Home
  | '10' // Telehealth - Provided Other than in Patient's Home;

export type LocationModifier =
  | 'GT' // Via interactive audio and video telecommunication systems
  | '95'; // Synchronous telemedicine service rendered via real-time interactive audio and video telecommunications system

export interface BillingCode {
  code: BillingCodeType;
  description: string;
  typicalDuration: number; // in minutes
  requiresSupervision: boolean;
  allowsTelehealth: boolean;
}

export interface BillingEntry {
  code: BillingCodeType;
  placeOfService: PlaceOfService;
  locationModifier?: LocationModifier;
  startTime: Date;
  endTime: Date;
  duration: number; // in minutes
  unitsOfService: number; // 1 unit = 15 minutes
  providerName: string;
  providerCredentials: string;
  providerTaxId?: string;
}

// Standard billing codes with descriptions
export const BILLING_CODES: Record<BillingCodeType, BillingCode> = {
  '97151': {
    code: '97151',
    description: 'Behavior identification assessment (BCBA/BCaBA)',
    typicalDuration: 90,
    requiresSupervision: false,
    allowsTelehealth: false,
  },
  '97152': {
    code: '97152',
    description: 'Behavior identification supporting assessment (RBT)',
    typicalDuration: 60,
    requiresSupervision: true,
    allowsTelehealth: false,
  },
  '97153': {
    code: '97153',
    description: 'Adaptive behavior treatment by protocol (RBT)',
    typicalDuration: 60,
    requiresSupervision: true,
    allowsTelehealth: true,
  },
  '97154': {
    code: '97154',
    description: 'Group adaptive behavior treatment by protocol',
    typicalDuration: 60,
    requiresSupervision: true,
    allowsTelehealth: true,
  },
  '97155': {
    code: '97155',
    description: 'Adaptive behavior treatment with protocol modification (BCBA)',
    typicalDuration: 60,
    requiresSupervision: false,
    allowsTelehealth: true,
  },
  '97156': {
    code: '97156',
    description: 'Family adaptive behavior treatment guidance',
    typicalDuration: 60,
    requiresSupervision: false,
    allowsTelehealth: true,
  },
  '97157': {
    code: '97157',
    description: 'Multiple family group adaptive behavior treatment guidance',
    typicalDuration: 60,
    requiresSupervision: false,
    allowsTelehealth: true,
  },
  '97158': {
    code: '97158',
    description: 'Group adaptive behavior treatment with protocol modification',
    typicalDuration: 60,
    requiresSupervision: false,
    allowsTelehealth: true,
  },
};

// Place of service descriptions
export const PLACE_OF_SERVICE: Record<PlaceOfService, string> = {
  '11': 'IN PERSON-CLINIC (11 - Office)',
  '12': 'IN PERSON-HOME/DAYCARE (12 - Home)',
  '03': 'IN PERSON-SCHOOL (03 - School)',
  '02': 'TELEHEALTH-CLINIC/SCHOOL (02 - Telehealth Provided in Patient\'s Home)',
  '10': 'TELEHEALTH-HOME/DAYCARE (10 - Telehealth Provided Other than in Patient\'s Home)',
};

// Calculate units of service from duration (1 unit = 15 minutes)
export const calculateUnits = (durationMinutes: number): number => {
  // Round to nearest 15-minute increment
  const roundedMinutes = Math.round(durationMinutes / 15) * 15;
  return roundedMinutes / 15;
};

// Calculate duration from start and end time
export const calculateDuration = (startTime: Date, endTime: Date): number => {
  const diff = endTime.getTime() - startTime.getTime();
  return Math.round(diff / (1000 * 60)); // Convert to minutes
};

// Validate billing entry
export const validateBillingEntry = (entry: Partial<BillingEntry>): string[] => {
  const errors: string[] = [];

  if (!entry.code) {
    errors.push('Billing code is required');
  }

  if (!entry.placeOfService) {
    errors.push('Place of service is required');
  }

  if (!entry.startTime) {
    errors.push('Start time is required');
  }

  if (!entry.endTime) {
    errors.push('End time is required');
  }

  if (entry.startTime && entry.endTime && entry.endTime <= entry.startTime) {
    errors.push('End time must be after start time');
  }

  if (!entry.providerName || !entry.providerCredentials) {
    errors.push('Provider name and credentials are required');
  }

  return errors;
};
